import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import { useSidebar } from '../../contexts/SidebarContext';

const Sidebar: React.FC = () => {
    const currentUser = useSelector((state: RootState) => state.auth.user);
    const isOwner = currentUser?.role === 'owner';
    const isAdmin = currentUser?.role === 'admin';
    const isOwnerOrAdmin = isOwner || isAdmin;
    const location = useLocation();
    const { isCollapsed, setIsCollapsed } = useSidebar();
    const [touchStart, setTouchStart] = useState<number | null>(null);
    const [touchEnd, setTouchEnd] = useState<number | null>(null);

    // Minimum swipe distance (in px) to trigger sidebar
    const minSwipeDistance = 50;

    const menuItems = [
        { path: '/cashier', icon: '💰', label: 'Cashier', show: true },
        { path: '/inventory', icon: '📦', label: 'Inventory', show: true },
        { path: '/customers', icon: '👥', label: 'Customers', show: true },
        { path: '/stock', icon: '📊', label: 'Stock Management', show: true },
        { path: '/opname', icon: '📋', label: 'Stock Opname', show: true },
        { path: '/purchase-orders', icon: '🛒', label: 'Purchase Orders', show: true },
        { path: '/mutations', icon: '🔄', label: 'Mutations', show: true },
        { path: '/returns', icon: '↩️', label: 'Returns', show: true },
        { path: '/reports', icon: '📈', label: 'Reports', show: true },
        { path: '/branches', icon: '🏪', label: 'Branches', show: isOwner },
        { path: '/users', icon: '👤', label: 'Users', show: isOwnerOrAdmin },
        { path: '/activity-logs', icon: '📊', label: 'Activity Logs', show: isOwner },
        { path: '/backups', icon: '💾', label: 'Backup Management', show: isOwnerOrAdmin },
        { path: '/profile', icon: '⚙️', label: 'Profile', show: true },
    ];

    // Handle swipe gestures
    const onTouchStart = (e: TouchEvent) => {
        setTouchEnd(null);
        setTouchStart(e.targetTouches[0].clientX);
    };

    const onTouchMove = (e: TouchEvent) => {
        setTouchEnd(e.targetTouches[0].clientX);
    };

    const onTouchEnd = () => {
        if (!touchStart || !touchEnd) return;
        
        const distance = touchEnd - touchStart;
        const isSwipeRight = distance > minSwipeDistance;
        const isSwipeLeft = distance < -minSwipeDistance;
        
        // Swipe right from left edge to open
        if (isSwipeRight && touchStart < 50 && isCollapsed) {
            setIsCollapsed(false);
        }
        // Swipe left to close
        if (isSwipeLeft && !isCollapsed) {
            setIsCollapsed(true);
        }
    };

    useEffect(() => {
        // Add touch event listeners
        document.addEventListener('touchstart', onTouchStart);
        document.addEventListener('touchmove', onTouchMove);
        document.addEventListener('touchend', onTouchEnd);

        return () => {
            document.removeEventListener('touchstart', onTouchStart);
            document.removeEventListener('touchmove', onTouchMove);
            document.removeEventListener('touchend', onTouchEnd);
        };
    }, [touchStart, touchEnd, isCollapsed]);

    return (
        <>
            {/* Backdrop for mobile when sidebar is open */}
            {!isCollapsed && (
                <div 
                    className="fixed inset-0 bg-black/50 z-30 md:hidden"
                    onClick={() => setIsCollapsed(true)}
                />
            )}

            {/* Sidebar */}
            <div 
                className={`fixed left-0 top-0 h-full bg-gray-900 text-white transition-all duration-300 z-40 shadow-2xl ${
                    isCollapsed ? '-translate-x-full md:translate-x-0 md:w-16' : 'translate-x-0 w-64'
                }`}
            >
                {/* Header */}
                <div className="p-4 border-b border-gray-700 flex items-center justify-between">
                    {!isCollapsed && (
                        <h2 className="font-bold text-lg">Navigation</h2>
                    )}
                    <button
                        onClick={() => setIsCollapsed(!isCollapsed)}
                        className="p-2 hover:bg-gray-800 rounded-lg transition-colors ml-auto"
                        title={isCollapsed ? 'Expand Sidebar' : 'Collapse Sidebar'}
                        aria-label={isCollapsed ? 'Expand Sidebar' : 'Collapse Sidebar'}
                    >
                        {isCollapsed ? (
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                        ) : (
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        )}
                    </button>
                </div>

                {/* Swipe Hint - Only show on mobile when collapsed */}
                {isCollapsed && (
                    <div className="fixed left-0 top-1/2 -translate-y-1/2 z-20 md:hidden pointer-events-none">
                        <div className="bg-blue-600/30 backdrop-blur-sm text-white text-xs py-2 px-1 rounded-r-lg border-r-2 border-blue-400 shadow-lg animate-pulse">
                            <div className="flex flex-col items-center gap-1">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                                <span className="writing-mode-vertical text-[10px]">Swipe</span>
                            </div>
                        </div>
                    </div>
                )}

                {/* Menu Items */}
                <nav className="py-4 overflow-y-auto h-[calc(100vh-73px)]">
                    <ul className="space-y-1">
                        {menuItems.filter(item => item.show).map((item) => {
                            const isActive = location.pathname === item.path || 
                                           (item.path !== '/cashier' && location.pathname.startsWith(item.path));
                            
                            return (
                                <li key={item.path}>
                                    <Link
                                        to={item.path}
                                        className={`flex items-center px-4 py-3 transition-colors group ${
                                            isActive 
                                                ? 'bg-blue-600 text-white' 
                                                : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                                        }`}
                                        title={isCollapsed ? item.label : ''}
                                    >
                                        <span className="text-xl flex-shrink-0">{item.icon}</span>
                                        {!isCollapsed && (
                                            <span className="ml-3 text-sm font-medium truncate">
                                                {item.label}
                                            </span>
                                        )}
                                    </Link>
                                </li>
                            );
                        })}
                    </ul>
                </nav>
            </div>

            {/* Mobile Floating Toggle Button (visible when collapsed on small screens) */}
            {isCollapsed && (
                <button
                    onClick={() => setIsCollapsed(false)}
                    className="fixed left-2 top-20 z-30 md:hidden bg-blue-600 text-white p-3 rounded-full shadow-2xl hover:bg-blue-700 transition-all hover:scale-110 active:scale-95"
                    title="Show Menu"
                    aria-label="Open Navigation Menu"
                >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                    </svg>
                </button>
            )}
        </>
    );
};

export default Sidebar;